import { PROJECT_ROLE_PERMISSIONS } from '@symbo.ls/sdk'

export function getCanvas(childKey) {
  const Canvas = this.getRootData('Canvas') || this.lookup('Canvas') || this.lookdown('Canvas')
  if (!Canvas) {
    return
  }

  if (childKey) {
    return Canvas[childKey]
  }
  return Canvas
}

export function getPlayground() {
  const Playground = this.getCanvas()?.parent
  if (!Playground) {
    return
  }
  return Playground
}

export function getVersionsAside() {
  const Playground = this.getPlayground()
  if (!Playground) {
    return
  }
  return Playground.parent?.Versions?.VersionsAside
}

export function getLocalStorageCanvasData() {
  const localStorageKey = `${this.getAppKey()?.split('.')[0]}__data`
  return this.call('getLocalStorage', localStorageKey)
}

export function getCanvasScope(childKey) {
  // canvas's v2 body when canvas is loaded (one owner per name — canvas registers canvasGetCanvasScope)
  const _canvasFork = this?.context?.methods?.canvasGetCanvasScope
  if (typeof _canvasFork === 'function') return _canvasFork.apply(this, arguments)
  const canvas = this.getCanvas()
  const scope = canvas?.scope

  if (childKey) {
    return scope?.[childKey]
  }

  return scope
}

export function setCanvasScope(obj) {
  const scope = this.getCanvasScope()
  // The editor chrome (CanvasEditor.onInit, QuickEditor, …) can run before —
  // or without — the Canvas scene having built its scope (in-shell mount
  // order, or a scene whose load failed). Writing into a nonexistent scope
  // was a hard TypeError inside `overwrite`, re-thrown on every render retry
  // — observed as a 1/s exception storm on the in-shell project route. No
  // scope yet → no-op; the caller's next write after the scene mounts lands.
  if (!scope) return null
  this.call('overwrite', scope, obj)
  this.syncCanvas(obj)
  return scope
}

export function getActiveCanvasPage() {
  return this.getCanvasScope('activeCanvasPage')
}

export function getCanvasFrame() {
  return this.getCanvas()?.Iframe
}

export function getCanvasFrameWindow() {
  return this.getCanvasFrame()?.node.contentWindow
}

export function getEditorPanels() {
  return this.getCanvas()?.parent?.Chosen?.Flex?.EditorPanels
}

export function getComponentPanelSwitcher() {
  return this.getEditorPanels()?.ComponentEditor.PanelSwitcher
}

export function getComponentCodeEditor() {
  return this.getComponentPanelSwitcher()?.content?.CodePreviewWidget.Monaco
}

export function getDocsView() {
  return this.getChosenScene()?.DocsView
}

export function getDynamicIsland() {
  return this.getPlayground()?.DynamicIsland
}

export function getQuickEditor() {
  const playground = this.getPlayground()
  return (
    playground?.QuickEditor ||
    playground?.CanvasEditor?.QuickEditor ||
    this.getRoot()?.lookdown?.('QuickEditor')
  )
}

export function getFloatingPanelElement() {
  return this.getPlayground()?.FloatingPanel
}

export function getCanvasModal() {
  return this.getPlayground()?.CanvasModal
}

export function getCanvasNavbar() {
  return this.getPlayground()?.MainCanvasNavbar
}

export function getFilesSidebar() {
  // return this.getDynamicIsland()?.CanvasProjectSidebar
  return this.getDynamicIsland()?.content
}

export function getFilesSidebarPrompt() {
  return this.getFilesSidebar()?.FilesSidebarPrompt
}

export function getCanvasPages() {
  if (this.isEmbed()) return this.getCanvas()?.ChooseCanvas
  return this.getCanvasNavbar()?.CanvasPagesDropdown
}

export function getComponentEditor() {
  return this.getEditorPanels()?.ComponentEditor
}

export function getChosenElement() {
  return this.getPlayground()?.Chosen
}

export function getChosenData(key) {
  const data = this.getChosenElement()?.data
  return key ? data[key] : data
}

export function getChosenElementContext() {
  return this.getChosenElement()?.data.chosen?.context
}

export function getChosenScene() {
  return this.getChosenElement()?.Flex?.ChosenScene
}

export function getChosenViewSvitcher() {
  return this.getChosenScene()?.ViewSvitcher
}

export function getChosenIframe() {
  return this.getChosenScene()?.ViewSvitcher?.ComponentFrameCanvas.Iframe
}

export function getChosenIframeWindow() {
  return this.getChosenIframe()?.node.contentWindow
}

export function getChosenSceneNavbar() {
  return this.getChosenElement()?.SceneNavbar
}

export function getCanvasCollection() {
  // canvas's v2 body when canvas is loaded (one owner per name — canvas registers canvasGetCanvasCollection)
  const _canvasFork = this?.context?.methods?.canvasGetCanvasCollection
  if (typeof _canvasFork === 'function') return _canvasFork.apply(this, arguments)
  return this.getCanvasScope('Collection')
}

export function getCanvasContext() {
  return this.getCanvasCollection().context
}

export function activateHovered(val) {
  // canvas's v2 body when canvas is loaded (one owner per name — canvas registers canvasActivateHovered)
  const _canvasFork = this?.context?.methods?.canvasActivateHovered
  if (typeof _canvasFork === 'function') return _canvasFork.apply(this, arguments)
  if (!this.call('isDefined', val)) {
    val = this
  }
  const data = this.getCanvasScope()
  const hovered = (data.hovered = this.call('isDefined', val) ? val : null)
  return this.syncCanvas({ hovered })
}

export function activateSelected(val, opts = {}) {
  // canvas's v2 body when canvas is loaded (one owner per name — canvas registers canvasActivateSelected)
  const _canvasFork = this?.context?.methods?.canvasActivateSelected
  if (typeof _canvasFork === 'function') return _canvasFork.apply(this, arguments)
  const data = this.getCanvasScope()

  if (!this.call('isDefined', val)) {
    val = this
  } else if (this.call('isString', val)) {
    const type = this.getType(val)
    const isOnCurrent = this.call('inOnCurrentCanvasPage', type, val)
    if (!isOnCurrent) {
      const onOtherCanvasPage = this.call('findKeyInOtherCanvasPages', type, val)
      if (onOtherCanvasPage) {
        this.call('openCanvasPage', onOtherCanvasPage)
      }
    }
    val = this.call('findComponentOnCanvas', val)
  }
  const selected = (data.selected = this.call('isDefined', val) ? val : data.hovered)
  if (selected) {
    this.changeCodePriority('component', opts)
    if (this.getUserSettings('autoSelectSidebar')) {
      if (this.call('getSidebarContent') !== 'ARTBOARD_TYPES')
        this.call('setSidebarContent', 'ARTBOARD_TYPES')
    }
  }
  this.updateSelectedLabel()
  return !opts.preventSync ? this.syncCanvas({ selected }) : data
}

export function updateSelectedLabel() {
  if (this.isEmbed()) return
  if (this.getFilesSidebar()?.FilesSidebarPrompt?.state?.focused) {
    this.getFilesSidebar().FilesSidebarPrompt.Label.SearchOverlay.SelectedResource.update()
  }
}

export function activateChosen(val, opts = {}) {
  // canvas's v2 body when canvas is loaded (one owner per name — canvas registers canvasActivateChosen)
  const _canvasFork = this?.context?.methods?.canvasActivateChosen
  if (typeof _canvasFork === 'function') return _canvasFork.apply(this, arguments)
  const data = this.getCanvasScope()
  if (!this.call('isDefined', val)) {
    val = this
  } else if (this.call('isString', val)) {
    const type = this.getType(val)
    const isOnCurrent = this.call('inOnCurrentCanvasPage', type, val)
    let onOtherCanvas = null
    if (isOnCurrent) {
      onOtherCanvas = null
    } else {
      onOtherCanvas = this.call('findKeyInOtherCanvasPages', type, val)
    }
    if (onOtherCanvas) {
      this.call('openCanvasPage', onOtherCanvas)
    }
    val = data.Collection?.lookdown((el) => el.state?.key === val)
  }
  const chosen = (data.chosen = this.call('isDefined', val) ? val : data.selected)
  if (chosen && chosen.state) {
    const { key } = chosen.state
    // this.addToTabs(key)
    this.updateRecents(key)
    this.changeCodePriority('component', opts)
    if (this.call('getSidebarContent') !== 'ARTBOARD_TYPES')
      this.call('setSidebarContent', 'ARTBOARD_TYPES')
  }
  return !opts.preventSync ? this.syncCanvas({ chosen }) : data
}

export function activateContext2(ev, val) {
  if (ev?.stopPropagation) {
    ev.stopPropagation()
  }

  const data = this.getCanvasScope()

  const isString = this.call('isString', val)
  const isDefined = this.call('isDefined', val)

  const isNull = val === null
  if (!ev || isNull) {
    return this.syncCanvas({
      inContext: null,
      positions: {
        contextX: null,
        contextY: null
      }
    })
  }

  if (isString) {
    const onOtherCanvas = this.call('findKeyInOtherCanvasPages', val) || 'Default'
    if (onOtherCanvas) {
      this.call('openCanvasPage', onOtherCanvas)
    }
    val =
      this.getCanvasCollection().lookdown((el) => el.state?.key === val) ||
      this.getItem(val) ||
      this.getHovered()
  } else if (!isDefined) {
    val = this
  }

  const inContext = (data.inContext = val)
  const { clientX, clientY } = ev

  return this.syncCanvas({
    inContext: inContext || null,
    positions: {
      contextX: clientX || null,
      contextY: clientY || null
    }
  })
}

// export function activateContextInSidebar(ev, val) {
export function activateContext(ev, val, preventDefault = true, stopPropagation = true) {
  // canvas's v2 body when canvas is loaded (one owner per name — canvas registers canvasActivateContext)
  const _canvasFork = this?.context?.methods?.canvasActivateContext
  if (typeof _canvasFork === 'function') return _canvasFork.apply(this, arguments)
  if (ev?.stopPropagation && stopPropagation) {
    ev.stopPropagation()
  }
  if (ev?.preventDefault && preventDefault) {
    ev.preventDefault()
  }

  const data = this.getCanvasScope()

  const isString = this.call('isString', val)
  const isDefined = this.call('isDefined', val)

  // THE ARMED RECORD LIVES ON THE CANVAS SCOPE, not on `this`.
  // The invocation that OPENS the menu and the one that CLOSES it run on
  // DIFFERENT elements: a sidebar row opens (`el.activateContext(ev, el)`),
  // while the Escape shortcut and the sidebar menu close with
  // `activateContext(null)` on another element. A pair stored on the opener
  // is `undefined` on the closer, `removeEventListener` never runs, and every
  // right-click leaves two capture-phase window listeners subscribed — each
  // holding a closure over its element. `getCanvasScope()` answers ONE object
  // for the whole canvas (it already carries `inContext`), so both reach it.
  //
  // The record holds `clientX` as a NUMBER. The handler outlives the click,
  // and reading `ev.clientX` inside it would retain the whole MouseEvent
  // (target, view, composed path) for as long as the menu stays open.
  const disarm = () => {
    const armed = data?._contextMenuArmed
    if (!armed) return
    window.removeEventListener('scroll', armed.reposition, true)
    window.removeEventListener('resize', armed.reposition, true)
    data._contextMenuArmed = null
  }

  const clearContextMenu = () => {
    disarm()
    return this.syncCanvas({
      inContext: null,
      positions: {
        contextX: null,
        contextY: null
      }
    })
  }

  const isNull = val === null
  if (!ev || isNull) {
    return clearContextMenu()
  }

  if (isString) {
    const type = this.getType(val)
    const isOnCurrent = this.call('inOnCurrentCanvasPage', type, val)
    let onOtherCanvas = null
    if (!isOnCurrent) {
      onOtherCanvas = this.call('findKeyInOtherCanvasPages', val)
    }
    if (onOtherCanvas) {
      this.call('openCanvasPage', onOtherCanvas)
    }
    val =
      this.getCanvasCollection().lookdown((el) => el.state?.key === val) ||
      this.getItem(val) ||
      this.getHovered()
  } else if (!isDefined) {
    val = this
  }

  const inContext = (data.inContext = val)
  const { clientX, clientY } = ev

  // Places the menu beside its target. Answers `true` when the target sits
  // outside the sidebar viewport, and places nothing in that case.
  const place = () => {
    const armed = data._contextMenuArmed
    if (!armed || !armed.target) return false
    // The menu mounts AFTER this open syncs `inContext`, so it cannot be
    // resolved up front. Resolve it on first use and keep it: a tree walk per
    // scroll event is up to 60 walks a second over the whole playground. A
    // disposed element (`node`/`parent` cleared) is resolved again.
    let ContextMenu = armed.ContextMenu
    if (!ContextMenu || !ContextMenu.node || !ContextMenu.parent) {
      ContextMenu = armed.ContextMenu = this.getPlayground()?.lookdown('ContextMenu')
    }
    const Overflow = this.getFilesSidebar()?.Overflow
    if (!ContextMenu || !ContextMenu.node || !Overflow) return false

    const PANEL_OFFSET_TOP = 10
    const targetRect = armed.target.getBoundingClientRect()
    const overflowRect = Overflow.node.getBoundingClientRect()
    const contextMenuHeight = ContextMenu.node.getBoundingClientRect().height
    const viewportHeight = window.innerHeight
    let topPosition = targetRect.top + targetRect.height / 2

    if (topPosition + contextMenuHeight > viewportHeight) {
      topPosition = viewportHeight - contextMenuHeight - PANEL_OFFSET_TOP
    }

    const isOutOfBounds =
      targetRect.bottom > overflowRect.bottom || targetRect.top < overflowRect.top

    if (!isOutOfBounds) {
      ContextMenu.setNodeStyles({
        transform: `translate(${armed.clientX}px, ${topPosition}px)`
      })
    }
    return isOutOfBounds
  }

  // ONE handler for both events: scroll and resize ask the same question and
  // act the same way. A target that SCROLLED out of the sidebar viewport
  // leaves the menu nothing to point at, so it closes.
  const reposition = () => {
    if (place()) clearContextMenu()
  }

  // Disarm whatever a previous right-click left subscribed before arming a
  // new pair. Two right-clicks with no close between them are ordinary use
  // (right-click one node, then another), and without this they would stack
  // a second pair on top of the first.
  disarm()
  data._contextMenuArmed = {
    reposition,
    target: val && val.node ? val.node : null,
    clientX
  }
  window.addEventListener('scroll', reposition, true)
  window.addEventListener('resize', reposition, true)

  // The first placement only PLACES. A row the sidebar clips at its edge is
  // already "out of bounds" when the user right-clicks it, and closing here
  // would make its menu impossible to open.
  requestAnimationFrame(() => {
    place()
  })

  return this.syncCanvas({
    inContext: inContext || null,
    positions: {
      contextX: clientX || null,
      contextY: clientY || null
    }
  })
}

export function changeCodePriority(val, opts = {}) {
  if (!this.call('isDefined', val)) {
    val = this
  }
  const data = this.getCanvasScope()
  const priorityCode = (data.priorityCode = val)
  return !opts.preventSync ? this.syncCanvas({ priorityCode }) : data
}

export function updateRecents(key) {
  const platformState = this.getRootState()
  const { appKey } = platformState
  const { recents } = platformState
  const recentExists = recents.indexOf(key)
  if (recentExists > -1) {
    this.call('removeFromArray', recents, recentExists)
  }
  if (key) {
    recents.unshift(key)
    if (appKey) {
      window.localStorage.setItem(
        `recents_${appKey}${platformState.branch}`,
        JSON.stringify(recents)
      )
    }
  }
}

export function activateFunction(val) {
  // canvas's v2 body when canvas is loaded (one owner per name — canvas registers canvasActivateFunction)
  const _canvasFork = this?.context?.methods?.canvasActivateFunction
  if (typeof _canvasFork === 'function') return _canvasFork.apply(this, arguments)
  if (!this.call('isDefined', val)) {
    val = this
  }
  const data = this.getCanvasScope()
  const activeFunction = (data.activeFunction = val)
  if (activeFunction) {
    this.changeCodePriority('function')
  }
  return this.syncCanvas({ activeFunction })
}

export function activateState(val) {
  if (!this.call('isDefined', val)) {
    val = this
  }
  const data = this.getCanvasScope()
  const activeState = (data.activeState = val)
  if (activeState) {
    this.changeCodePriority('state')
  }
  return this.syncCanvas({ activeState })
}

export function activateDs(val) {
  if (!this.call('isDefined', val)) {
    val = this
  }
  const data = this.getCanvasScope()
  const activeDs = (data.activeDs = val)
  if (activeDs) {
    this.changeCodePriority('designSystem')
  }
  return this.syncCanvas({ activeDs })
}

export function getHovered() {
  return this.getCanvasScope('hovered')
}

export function getHoveredKey() {
  // canvas's v2 body when canvas is loaded (one owner per name — canvas registers canvasGetHoveredKey)
  const _canvasFork = this?.context?.methods?.canvasGetHoveredKey
  if (typeof _canvasFork === 'function') return _canvasFork.apply(this, arguments)
  return this.getHovered()?.state?.key
}

export function getSelected() {
  // canvas's v2 body when canvas is loaded (one owner per name — canvas registers canvasGetSelected)
  const _canvasFork = this?.context?.methods?.canvasGetSelected
  if (typeof _canvasFork === 'function') return _canvasFork.apply(this, arguments)
  return this.getCanvasScope('selected')
}

export function getSelectedKey() {
  // canvas's v2 body when canvas is loaded (one owner per name — canvas registers canvasGetSelectedKey)
  const _canvasFork = this?.context?.methods?.canvasGetSelectedKey
  if (typeof _canvasFork === 'function') return _canvasFork.apply(this, arguments)
  return this.getSelected()?.state?.key
}

export function getChosen() {
  // canvas's v2 body when canvas is loaded (one owner per name — canvas registers canvasGetChosen)
  const _canvasFork = this?.context?.methods?.canvasGetChosen
  if (typeof _canvasFork === 'function') return _canvasFork.apply(this, arguments)
  return this.getCanvasScope('chosen')
}

export function getChosenKey() {
  // canvas's v2 body when canvas is loaded (one owner per name — canvas registers canvasGetChosenKey)
  const _canvasFork = this?.context?.methods?.canvasGetChosenKey
  if (typeof _canvasFork === 'function') return _canvasFork.apply(this, arguments)
  const chosen = this.getChosen()
  return this.call('isString', chosen) ? chosen : chosen?.state?.key
}

export function getInContext() {
  return this.getCanvasScope('inContext')
}

export function getInContextKey() {
  // canvas's v2 body when canvas is loaded (one owner per name — canvas registers canvasGetInContextKey)
  const _canvasFork = this?.context?.methods?.canvasGetInContextKey
  if (typeof _canvasFork === 'function') return _canvasFork.apply(this, arguments)
  return this.getInContext()?.state?.key
}

export function getCanvasPositions() {
  // canvas's v2 body when canvas is loaded (one owner per name — canvas registers canvasGetCanvasPositions)
  const _canvasFork = this?.context?.methods?.canvasGetCanvasPositions
  if (typeof _canvasFork === 'function') return _canvasFork.apply(this, arguments)
  return this.getCanvasScope('positions') || {}
}

export function zoomToNode(zoomedElement, opts = {}) {
  // canvas's v2 body when canvas is loaded (one owner per name — canvas registers canvasZoomToNode)
  const _canvasFork = this?.context?.methods?.canvasZoomToNode
  if (typeof _canvasFork === 'function') return _canvasFork.apply(this, arguments)
  const canvasScope = this.getCanvasScope()
  const frameWindow = this.getCanvasFrameWindow()
  const { innerWidth, innerHeight } = frameWindow
  if (!canvasScope.panzoomInstance || !zoomedElement) {
    return
  }
  const getTransform = canvasScope.panzoomInstance.getTransform()

  if (opts.useZoom) {
    canvasScope.panzoomInstance.zoomAbs(getTransform.x, getTransform.y, 0.65)
  }

  setTimeout(() => {
    const zoomedNode = zoomedElement.node
    const { x, y, width, height } = zoomedNode.getBoundingClientRect()
    const panToX = x - (innerWidth - width) / 2
    const panToY = y - (innerHeight - height) / 2
    canvasScope.panzoomInstance.moveBy(-panToX, -panToY, true)
  }, 50)
}

// keep in methods, its needed in frame
export async function updateGridsInSchema(items) {
  const changes = items
    .map((item) => {
      if (!item.el?.ref) {
        console.error('[updateGridsInSchema] Missing element reference', item)
        return null
      }
      const { state } = item.el.ref
      if (!state || state.package) {
        return null // skip packaged components or invalid state
      }
      const type = this.getType(state)
      const path = ['schema', type, state.key, 'settings', 'gridOptions']
      const value = item._orig
      // Skip if value did not actually change – avoids unnecessary state churn
      const currentVal = state.settings?.gridOptions
      const isSame = currentVal && JSON.stringify(currentVal) === JSON.stringify(value)
      if (isSame) {
        return null
      }
      if (!state.settings) state.settings = {}
      state.settings.gridOptions = value
      return ['update', path, value]
    })
    .filter(Boolean)
  if (!changes.length) {
    return // nothing to apply
  }
  // Apply changes via CollabService but prevent a reactive run-through that would
  // immediately trigger another re-render, causing an infinite loop.
  // When the server eventually confirms (via commit) Canvas will still receive
  // the version update.
  try {
    this.sdk.updateData(changes, {
      skipCanvasUpdate: true,
      skipRuntimeTests: true,
      message: 'Updated gridOptions on artboards at ' + this.getActiveCanvasPage()
    })
  } catch (e) {
    // If the collab connection is not yet established we fall back to applying
    // the changes locally so that the UI stays in sync.
    if (this.sdk?.getService) {
      const collab = this.sdk.getService('collab')
      collab?._stateManager?.applyChanges(changes, { preventUpdate: true })
    }
  }
}

export function getCleanCanvasScope(data) {
  const obj = {}
  const allowedKeys = [
    'explorer',
    'editorPanels',
    'docs',
    'previewSize',
    'modal',
    'followingUser',
    'openUrl',
    'editorActive',
    'activeCanvasPage',
    'hoverLayer',
    'activeLayer',
    'positions',
    'chosenPos',
    'monaco',
    'monacoSelection',
    'preferredEditor',
    'editorActive',
    'priorityCode',
    'showLayersSchema'
  ]
  const elementToUpdate = this.getWindowKeys()
  const allowedNodes = ['hovered', 'selected', 'chosen', 'inContext']
  for (const prop in data) {
    const val = data[prop]
    const allowed = allowedKeys.includes(prop) || elementToUpdate.includes(prop)
    if (allowed) {
      obj[prop] = val
    }
    if (allowedNodes.includes(prop)) {
      obj[prop] = this.call('isString', val) ? val : val?.state?.key ? val?.state?.key : null
    }
  }
  return obj
}

export function syncSocketCanvasData(clientData, opts = {}) {
  const platformState = this.getRootState()
  const canvasData = this.getCanvasScope()
  if (!canvasData) {
    return
  }

  // The shell root state carries no `settings` object (the legacy canvas
  // root did), so every `syncCanvas` on the shell threw here AFTER the
  // localStorage write — one uncaught TypeError per scope sync, measured
  // live from `setCanvasScope` at boot and from the files-sidebar layer
  // persist (WS-FILES-SAVE-LAYERS-VISIBILITY-STATE-MISSING-1). No setting →
  // no socket forward, the same answer as `syncCanvas: false`.
  const { userId, settings, followingUser } = platformState
  if (!settings?.syncCanvas) {
    return
  }

  if (!followingUser || opts.forceSendSocket) {
    if (this.sdk?.isConnected()) {
      this.sdk.sendCursor({
        [userId]: {
          ...clientData
        }
      })
    }
  } else if (clientData.positions?.mouseX || clientData.chosenPos?.mouseX) {
    if (this.sdk?.isConnected()) {
      this.sdk.sendCursor({
        [userId]: {
          positions: clientData.positions?.mouseX && {
            mouseX: clientData.positions.mouseX,
            mouseY: clientData.positions.mouseY
          },
          chosenPos: clientData.chosenPos?.mouseX && {
            mouseX: clientData.chosenPos.mouseX,
            mouseY: clientData.chosenPos.mouseY
          }
        }
      })
    }
  }

  return clientData
}

export function syncCanvas(data = {}, opts = {}) {
  const platformState = this.getRootState()
  const canvasData = this.getCanvasScope()
  const { userId } = platformState

  const resetObjs = (key) => {
    if (data[key]) {
      data[key] = { ...canvasData[key], ...data[key] }
    }
  }

  resetObjs('positions')
  resetObjs('chosenPos')
  resetObjs('monaco')
  resetObjs('monacoSelection')

  // The files-sidebar layer-visibility map (`showLayersSchema`, see
  // getShowLayersSchema / saveLayersVisibilityState in editor.js) has no
  // boot-time writer, and nothing seeds the scope from `<app>__data` at boot,
  // so the FIRST sync of a fresh scope persisted the clean scope without it
  // and wiped the map — measured live on the shell: the map survived its
  // own write and died on the next reload, under the boot `setCanvasScope`.
  // Seed it once, before the write, from the copy the scope is seeded from
  // (WS-FILES-SAVE-LAYERS-VISIBILITY-STATE-MISSING-1).
  if (
    canvasData &&
    canvasData.showLayersSchema === undefined &&
    data.showLayersSchema === undefined
  ) {
    canvasData.showLayersSchema = this.getCanvasScopeFromStorageOrData()?.showLayersSchema || {}
  }

  // Merge incoming changes into current canvasData
  this.call('overwrite', canvasData, data)

  // Persist cleaned client state to rootState.clients
  if (!platformState.clients) platformState.clients = {}
  const reservedClientKeys = ['localhost', 'cli', 'platform']
  let clientKey = userId
  if (reservedClientKeys.includes(clientKey)) {
    clientKey = `user_${clientKey}`
  }
  const cleanData = (platformState.clients[clientKey] = this.getCleanCanvasScope(canvasData))

  // Store to localStorage when relevant ( skip continuous mouse move spam )
  const localStorageKey = `${this.getAppKey()?.split('.')[0]}__data`
  const changesWithValues = Object.values(cleanData).filter((v) => v).length
  if (!data.positions && changesWithValues) {
    this.call('setLocalStorage', localStorageKey, cleanData)
  }

  // Forward minimal delta via socket
  return this.call('syncSocketCanvasData', this.getCleanCanvasScope(data), opts)
}

export function syncCanvasPositions(positions) {
  const data = this.getCanvasScope()
  if (!data) {
    return
  }
  return this.syncCanvas({ positions })
}

export function canvasFade(condition) {
  const { Fade } = this.getCanvas()
  if (condition) {
    Fade.fadeIn.background = `var(--theme-modalFade-${this.getRootState(
      'globalTheme'
    )}-backgroundColor)`
    Fade.setNodeStyles(Fade.fadeIn)
  } else {
    Fade.setNodeStyles(Fade.fadeOut)
  }
}

export function toggleLoading() {
  const data = this.getCanvasScope()
  if (!data) return

  this.variables({ loading: data?.loading }).changed((_) => {
    if (data.loading) {
      this.parent.LoadingGifSection?.setNodeStyles?.(this.parent.LoadingGifSection?.fadeIn)
    } else {
      this.parent.LoadingGifSection?.setNodeStyles?.(this.parent.LoadingGifSection?.fadeOut)
    }
  })
}

export const resetCanvasPan = function resetCanvasPan() {
  const canvasScope = this.getCanvasScope()
  const activeCanvasPage = this.getActiveCanvasPage()
  const localStorageKey = `${this.getAppKey()?.split('.')[0]}___${activeCanvasPage}`
  const fromStorage = JSON.parse(
    (this.call('sameOriginTop')?.localStorage || window.localStorage).getItem(localStorageKey)
  )
  const { x, y, scale } = fromStorage || this.getCanvasPositions() || {}
  const { panzoomInstance } = canvasScope
  if (panzoomInstance) {
    panzoomInstance.moveTo(x || 0, y || 0, scale || 1)
    panzoomInstance.zoomAbs(x || 0, y || 0, scale || 1)
  }
}

export function getCanvasScopeFromStorageOrData() {
  const localStorageData = this.getLocalStorageCanvasData()
  const platformState = this.getRootState()
  const { userId } = platformState

  return localStorageData || platformState.clients?.[userId]
}

// export function resetCanvasFromStorage() {
//   const initData = this.getCanvasScopeFromStorageOrData()
//   if (!initData) return

//   let data = initData
//   if (!this.getUserSettings('allowInitialSelection')) {
//     const { selected, chosen, ...rest } = initData
//     data = rest
//   }
//   if (this.getRootState('selected'))
//     data.selected = this.getRootState('selected')
//   this.call('overwrite', this.getCanvasScope(), data)
// }

export function isDashboard() {
  const { pathname } = window.location
  if (pathname.startsWith('/dashboard')) return true
}

// Signed in = the boot gate's verdict (`context.bootSignedIn`, set by shared
// bootResolve from the server's answer), never the raw stored token — a stored
// token can be expired or revoked. Until the gate decides, nobody is. A host
// with no boot gate at all (an embed frame) keeps the token answer.
//
// After the boot, the LIVE session decides (one-boot review D4): once the
// shell's session pass is ready (root._authReady), root.signedIn follows an
// in-place sign-in and a soft sign-out — the boot verdict is the answer only
// until then.
export function isAuthorised() {
  const verdict = this.context && this.context.bootSignedIn
  if (typeof verdict === 'boolean') {
    const st = this.state && (this.state.root || this.state)
    if (st && st._authReady === true && typeof st.signedIn === 'boolean') return st.signedIn
    return verdict
  }
  if (this.context && this.context.bootReady) return false
  return Boolean(this.sdk?.getAuthToken())
}

export function isFree() {
  return Boolean(this.getRootState('tier') === 'free')
}

export function isCanvas(routeOverride) {
  const pathname = routeOverride || window.location.pathname
  if (pathname.startsWith('/canvas') || pathname.startsWith('/embed')) return true

  const [, user, project] = pathname.split('/')

  if (!user || !project || this.getCanvasPathname() !== `/${user}/${project}`) {
    return
  }

  return Boolean(this.getCanvasScope())
}

export function isEmbed() {
  return this.getRootState('isEmbed')
}

// The SDK's own role → permission table, imported rather than mirrored so the
// two cannot drift. Exported from the SDK's MAIN entry (sdk fe34793) because
// Parcel does not resolve package.json `exports` subpaths in this build.
//
// The SDK's `hasPermission` also consults the GLOBAL role table, but that one
// grants only `viewPublicProjects` / `governance` / `managePlatform` — none of
// the project-permission keys the UI gates on — so `projectRole` is the sole
// source of truth for these and this table is sufficient.

// Synchronous replacement for `el.sdk.hasPermission(perm)` in render props.
//
// `sdk.hasPermission` is synchronous INSIDE the SDK (a table lookup), but the
// shell wraps every SDK method in an init gate — `(...args) =>
// initPromise.then(() => value.apply(target, args))` — so through `el.sdk` it
// returns a PROMISE. `Boolean(promise)` is `true` unconditionally, which meant
// every UI permission gate rendered for everybody. Use this instead anywhere a
// permission is needed in a boolean position.
//
// UNKNOWN role deliberately reads as PERMITTED, matching `isEditMode` below.
// These gates hide affordances; they are not the authorization boundary (the
// server enforces that). A brief permissive window before `projectRole` lands
// closes on the next render, whereas denying an owner a control they own is a
// functional regression. Fail-open on "not yet known", fail-closed on "known
// and not allowed" — which is what actually closes the bypass, since a guest's
// or editor's role IS known.
// The embed is the one context where the old `el.sdk.hasPermission` gates
// actually worked: `el.sdk` there is the embed stub (iframeApp/
// embedCompatMethods.js), which is NOT wrapped in the init gate and returns a
// literal `false` for `hasPermission`. Preserve that contract — the embed is
// read-only and must never surface an editor affordance.
export function hasProjectPermission(perm) {
  if (this.isEmbed()) return false
  const role = this.getRootState('projectRole')
  if (!role) return true
  return (PROJECT_ROLE_PERMISSIONS[role] || []).includes(perm)
}

// `sdk.hasPermission` is ASYNC here and this method is not. The shell wraps
// every SDK method in an init gate — `(...args) => initPromise.then(() =>
// value.apply(target, args))` — so `hasPermission('editMode')` returns a
// PROMISE, and `Boolean(promise)` is true unconditionally. This method
// therefore returned "yes, editable" for everyone, including users whose
// permission resolves to false (verified live: the promise resolved `false`
// while the method still reported editable). Its consumers are sync render
// props — e.g. CanvasV2CodeEditor's `Monaco.readOnly: (el) => el.isReadOnly()`
// — so they cannot await it.
//
// Resolved synchronously from `projectRole` instead, the same signal `isAdmin`
// / `isOwner` below already use. `projectRole` is set by performProjectSwitch
// (shared/methods/project.js) and defaults to 'guest' for non-members.
//
// UNKNOWN role deliberately reads as EDITABLE, not read-only. The consuming
// bindings carry no `subscribeTo`, so they evaluate once at render and never
// re-run: locking the editor before the role lands would be UNRECOVERABLE for
// a legitimate editor, whereas a brief permissive window closes as soon as the
// role resolves on the next render. Fail-open on "not yet known", fail-closed
// on "known and not allowed" — that still fixes the actual bypass, because a
// guest's role IS known.
const EDIT_ROLES = ['owner', 'admin', 'editor']

export function isEditMode() {
  if (this.isEmbed()) return false
  const role = this.getRootState('projectRole')
  if (!role) return true
  return EDIT_ROLES.includes(role)
}

export function isAdmin() {
  return ['owner', 'admin'].includes(this.getRootState('projectRole'))
}

export function isOwner() {
  return ['owner'].includes(this.getRootState('projectRole'))
}

export function isReadOnly() {
  return !this.isEditMode()
}

export function preventLocationChanges(window) {
  // Intercept beforeunload/navigation attempts
  // window.addEventListener('beforeunload', (e) => {
  //   e.preventDefault()
  // })
  // const expectedUrl = this.getCanvasPathnameAnyway()
  // new MutationObserver(() => {
  //   if (window.location.href !== expectedUrl) {
  //     console.warn('Blocked forced navigation')
  //     window.location.replace(expectedUrl)
  //   }
  // }).observe(document, { childList: true, subtree: true })
}

export function isDocs() {
  const { pathname } = window.location
  if (['/docs', '/tutorials', '/index'].some((p) => pathname === p || pathname.startsWith(`${p}/`)))
    return true
}

export function isDevelopers() {
  const { pathname } = window.location
  if (['/developers'].some((p) => pathname === p || pathname.startsWith(`${p}/`))) return true
}

export function getCanvasPathname() {
  const { workingProjectOwner, workingProjectKey } = this.getRootState() || {}
  if (!workingProjectOwner || !workingProjectKey) return
  return `/${workingProjectOwner}/${workingProjectKey}`
}

export function getCanvasPathnameAnyway() {
  if (this.getCanvasPathname()) return this.getCanvasPathname()
  let { appKey, owner } = this.getRootState() || {}
  appKey = appKey?.split('.')[0]
  owner = owner?.username
  if (!owner) {
    const { members } = this.getRootState() || {}
    owner = members?.find((m) => m.role === 'owner')?.username
  }
  return appKey && owner ? `/${owner}/${appKey}` : '/studio'
}

export function getCanvasPathnameLocal() {
  const s = this.state
  return `/${s.owner?.username}/${s.key.split('.')[0]}`
}

export function openArtboardItem(type = 'components', key) {
  // canvas's v2 body when canvas is loaded (one owner per name — canvas registers canvasOpenArtboardItem)
  const _canvasFork = this?.context?.methods?.canvasOpenArtboardItem
  if (typeof _canvasFork === 'function') return _canvasFork.apply(this, arguments)
  if (!this.call('inOnCurrentCanvasPage', type, key)) {
    this.call('openCanvasPage', this.call('findKeyInOtherCanvasPages', key) || 'Default')
  }
  const t = window.setTimeout(() => {
    this.activateSelected(key)
    const data = this.getCanvasScope()
    const t2 = window.setTimeout(() => {
      this.zoomToNode(data.selected)
      clearTimeout(t2)
    }, 100)
    clearTimeout(t)
  }, 35)
}

export function openItem(type = 'components', key) {
  // canvas's v2 body when canvas is loaded (one owner per name — canvas registers canvasOpenItem)
  const _canvasFork = this?.context?.methods?.canvasOpenItem
  if (typeof _canvasFork === 'function') return _canvasFork.apply(this, arguments)
  if (this.call('hasType', 'ARTBOARD_TYPES', type)) {
    return this.call('openArtboardItem', type, key)
  }
  if (this.call('hasType', 'FUNCTION_TYPES', type)) {
    return this.activateFunction(key)
  }
  if (this.call('hasType', 'STATE_TYPES', type)) {
    return this.activateState(key)
  }
  if (this.call('hasType', 'ASSETS_TYPES', type)) {
    return this.call('openRouterModal', '/edit-file', {
      key: key
    })
  }
}

export function getCanvasMode() {
  return this.getCanvasScope('canvasMode')
}

export function isCanvasMode(mode) {
  // canvas's v2 body when canvas is loaded (one owner per name — canvas registers canvasIsCanvasMode)
  const _canvasFork = this?.context?.methods?.canvasIsCanvasMode
  if (typeof _canvasFork === 'function') return _canvasFork.apply(this, arguments)
  const canvasMode = this.getCanvasScope('canvasMode')
  if (mode === 'defaultMode') return !canvasMode || canvasMode === 'defaultMode'
  return canvasMode === mode
}

export function setCanvasMode(mode) {
  // canvas's v2 body when canvas is loaded (one owner per name — canvas registers canvasSetCanvasMode)
  const _canvasFork = this?.context?.methods?.canvasSetCanvasMode
  if (typeof _canvasFork === 'function') return _canvasFork.apply(this, arguments)
  return (this.getCanvasScope().canvasMode = mode)
}
